import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import {
  emailAtom,
  emailValidAtom,
  messageForLoginModalAtom,
} from "@/store/atoms";
import { useAuth, useVerifyEmail } from "common/hooks";
import { hasVerifiedSession, VerifyEmailError } from "common/apis";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import Loader from "@/components/common/Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import { Helmet } from "react-helmet-async";
import { useAfterLogin } from "@/features/auth/useAfterLogin";
import { isPendingVerification } from "@/features/auth/pendingVerification";

export const Route = createFileRoute("/verifyuser/$code/user/$email")({
  component: VerifyUser,
});

/** Exported for the route test; the router only reads `Route`. */
export function VerifyUser() {
  const navigate = useNavigate({ from: "/" });
  const { isLoading, isFetching, isLoggedIn } = useAuth();
  const isAuthLoading = isLoading || isFetching;
  const { showModal } = useAuthModals();
  const [, setEmail] = useAtom(emailAtom);
  const [, setEmailValid] = useAtom(emailValidAtom);
  const [, setMesageForLoginModal] = useAtom(messageForLoginModalAtom);
  const { code, email } = Route.useParams();
  const [verifyUserEmailInitiated, setVerifyUserEmailInitiated] =
    useState(false);

  // Decode the URL-encoded user parameter
  const decodedUserEmail = decodeURIComponent(email);

  const showToast = useCustomToast();
  const afterLogin = useAfterLogin();

  const {
    verifyUserEmail,
    error,
    isLoading: isVerifyLoading,
    result,
  } = useVerifyEmail();

  // Handled exactly once: the session commit makes authAtom refetch and `isLoggedIn` flip while
  // the lazy "/" chunk still loads, which would replay the branch (second event, second toast).
  const handled = useRef(false);

  useEffect(() => {
    if (isAuthLoading || verifyUserEmailInitiated || !code || !decodedUserEmail) return;

    // Sign in only when this browser started the verification and holds no session: a link
    // opened elsewhere (another device, a forwarded link) verifies and lands on the login step.
    verifyUserEmail({
      token: code,
      email: decodedUserEmail,
      login: !isLoggedIn && isPendingVerification(decodedUserEmail),
    });
    setVerifyUserEmailInitiated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthLoading, isLoggedIn, code, decodedUserEmail, verifyUserEmailInitiated]);

  useEffect(() => {
    if (!verifyUserEmailInitiated || isVerifyLoading || (!result && !error) || handled.current) return;
    handled.current = true;

    // Session committed by useVerifyEmail: finish the login and go home.
    if (hasVerifiedSession(result)) {
      afterLogin(result.data);
      showToast(result.message, "success");
      navigate({ to: "/" });
      return;
    }

    // Signed in before the click: never open the login modal over a live session.
    if (isLoggedIn) {
      showToast(
        result?.message ?? (error as Error).message,
        result ? "success" : "error",
      );
      navigate({ to: "/" });
      return;
    }

    // Every other outcome ends on the login step: a rejected link (expired, exhausted, wrong
    // or replaced) carries the server's reason, a verification without a session the thank-you.
    // The next password login re-issues a credential. This route never stays on screen.
    setEmail(decodedUserEmail);
    setEmailValid(true);
    if (error) {
      const rateLimited =
        error instanceof VerifyEmailError && error.code === "rate_limited";
      setMesageForLoginModal(
        rateLimited
          ? "You've tried to use verify too many times. Please try again in 5 minutes."
          : error.message,
      );
    } else {
      setMesageForLoginModal(result!.message);
    }
    showModal(AuthModalScreen.EnterEmail);
    navigate({ to: "/" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyUserEmailInitiated, isVerifyLoading, result, error]);

  return (
    <>
      <Helmet>
        <title>Verifying your email - Sociability</title>
      </Helmet>
      <div aria-busy={isAuthLoading || isVerifyLoading}>
        <h1 className="sr-only">Verifying your email</h1>
        {(isAuthLoading || isVerifyLoading) && <Loader />}
      </div>
    </>
  );
}
